import { Injectable } from '@angular/core';

import { Decimal } from 'decimal.js';

import { SlipTypeKeys } from '../../bet-generation/betslip-system-type';
import { ComboSlipType, SingleSlipType, SlipType, SystemSlipType } from '../../bet-generation/slip-type';
import { BetslipPickStorageLoader } from '../../picks/services/betslip-pick-storage-loader';
import { IBetslipOverAskSavedState, IBetslipOverAskState, defaultOverAskState } from '../state';

@Injectable()
export class BetslipOverAskStateStorageService {
    constructor(private pickLoader: BetslipPickStorageLoader) {}

    toSavedState(state: IBetslipOverAskState): IBetslipOverAskSavedState {
        return {
            offerId: state.offerId,
            flowState: state.flowState,
            bets: state.bets.map((b) => ({
                slipId: b.slipId,
                index: b.index,
                stake: b.stake.toNumber(),
                type: { basis: b.type.basis, key: b.type.key, name: b.type.name } as SlipType,
                isRemoved: b.isRemoved,
                changes: {
                    stake: b.changes.stake?.toNumber(),
                },
                promoTokens: b.promoTokens || [],
                edsPromoTokens: b.edsPromoTokens || [],
                picks: b.picks.map((p) => ({
                    index: p.index,
                    pick: p.pick.toJSON(),
                    isEachWay: p.isEachWay,
                    isBanker: p.isBanker,
                    price: {
                        nativeOdds: p.price.nativeOdds,
                        type: p.price.type,
                    },
                    oddsState: p.oddsState,
                    changes: {
                        price:
                            typeof p.changes.price === 'undefined'
                                ? void 0
                                : {
                                      nativeOdds: p.changes.price.nativeOdds,
                                      type: p.changes.price.type,
                                  },
                        marketType: p.changes.marketType,
                    },
                })),
            })),
            error: state.error,
            requestId: state.requestId,
            parentRequestId: state.parentRequestId,
            decisionSource: state.decisionSource,
            isofferModified: state.isofferModified,
        };
    }

    fromSavedState(state: IBetslipOverAskSavedState | undefined): IBetslipOverAskState {
        if (!state) {
            return { ...defaultOverAskState };
        }

        return {
            offerId: state.offerId,
            flowState: state.flowState,
            reviewState: {
                offerChanged: false, // Update after request to CDS
                remainingSeconds: -1, // Update after request to BPOS
            },
            bets: state.bets.map((b) => ({
                slipId: b.slipId,
                index: b.index,
                stake: new Decimal(b.stake),
                type:
                    b.type.key === SlipTypeKeys.Single
                        ? new SingleSlipType()
                        : b.type.key === SlipTypeKeys.Combo
                          ? new ComboSlipType()
                          : new SystemSlipType(b.type.key, b.type.name, b.type.basis),
                isRemoved: b.isRemoved,
                changes: {
                    stake: typeof b.changes.stake === 'undefined' ? void 0 : new Decimal(b.changes.stake),
                },
                promoTokens: b.promoTokens,
                edsPromoTokens: b.edsPromoTokens,
                picks: b.picks.map((p) => ({
                    index: p.index,
                    pick: this.pickLoader.loadPick(p.pick),
                    isEachWay: p.isEachWay,
                    isBanker: p.isBanker,
                    price: {
                        type: p.price.type,
                        nativeOdds: p.price.nativeOdds,
                    },
                    oddsState: p.oddsState,
                    changes: {
                        marketType: p.changes.marketType,
                        price:
                            typeof p.changes.price === 'undefined'
                                ? void 0
                                : {
                                      nativeOdds: p.changes.price.nativeOdds,
                                      type: p.changes.price.type,
                                  },
                    },
                })),
            })),
            error: state.error,
            requestId: state.requestId,
            parentRequestId: state.parentRequestId,
            isofferModified: state.isofferModified,
            decisionSource: state.decisionSource,
        };
    }
}
